import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { LuArrowRight, LuChevronLeft, LuChevronRight, LuTrophy, LuBriefcase, LuSun, LuX, LuPause, LuPlay, LuClock, LuUserPlus, LuMail } from "react-icons/lu";
import "../../styles/home/home.css";
import EventCard from "../events/eventCard";
import useEvents from "../../utils/useEvents";
import { API_URL } from "../../utils/api";
import { getCategoryLabel, getCurrentEvents } from "../../utils/events";
import { PRIVATE_PARTNERS } from "../../utils/partners";
import { getStoredNow } from "../../utils/date";
import { EVENT_CATEGORY_SELECTED } from "../../reducers/eventCategoryReducer";




//ACCÈS RAPIDES SOUS LE BANDEAU D'ACCUEIL (les chiffres du club sont sur la page Club)
const QUICK_LINKS = [
	{ to: "/infoscription", icon: LuClock, tone: "red", title: "Horaires d’entraînement", text: "Jours, créneaux et gymnases" },
	{ to: "/registration", icon: LuUserPlus, tone: "green", title: "S’inscrire", text: "Licence et cotisation en ligne" },
	{ to: "/beach", icon: LuSun, tone: "sand", title: "Beach-volley", text: "Entraînements d’été à Socoa" },
	{ to: "/contact", icon: LuMail, tone: "ink", title: "Nous contacter", text: "Une question ? Écrivez-nous" },
];

const CLUB_PILLARS = [
	{ icon: LuTrophy, tone: "red", title: "Compétition", text: "4 équipes proxy engagées en championnat, entraînements encadrés toute la saison." },
	{ icon: LuBriefcase, tone: "green", title: "Corpo & loisir", text: "5 équipes corpo pour jouer entre collègues ou entre amis, dans la bonne humeur." },
	{ icon: LuSun, tone: "sand", title: "Beach-volley", text: "Sessions et tournois sur le sable dès les beaux jours, face à l’océan." },
];

function Home() {

	//TOUS LES ÉVÈNEMENTS (mémoire du navigateur puis API)
	const allEvents = useEvents();
	const [commingFlashNews, setCommingFlashNews] = useState([])
	const [showFlashNews, setShowFlashNews] = useState(false)
	const [isFlashPaused, setIsFlashPaused] = useState(false)

	//POUR LE DÉFILEMENT DES ÉVÈNEMENTS
	const eventsTrackRef = useRef(null);
	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);

	//POUR LA NAVIGATION
	const navigate = useNavigate();
	//POUR DISPATCHER LES REDUCERS
	const dispatch = useDispatch();


	//LES FLASH NEWS : récupérées de la base de données, affichées dans un bandeau sous le menu
	useEffect(() => {
		const getCommingFlashNews = async () => {
			try {
				//API_URL : localhost en développement, api.vblc.fr en production (voir utils/api.js)
				const data = await fetch(`${API_URL}/flashNewsRoute/loadFlashNews`)
				const body = await data.json()
				//On ne garde que les flash news en cours d'affichage (heure locale, voir utils/date.js)
				const now = getStoredNow();
				const currentFlashNews = body.filter((news) => new Date(news.startDate) <= now && now < new Date(news.endDate));
				if (currentFlashNews.length !== 0) {
					setCommingFlashNews(currentFlashNews);
					setShowFlashNews(true)
				}
			} catch (error) {
				//API injoignable : pas de bandeau flash news
			}
		}
		getCommingFlashNews();
	}, []);


	//ÉVÈNEMENTS EN COURS D'AFFICHAGE (entre leur début et leur fin d'affichage), du plus proche au plus lointain
	const futureEvents = getCurrentEvents(allEvents);


	//ACTIVER / DÉSACTIVER LES FLÈCHES SELON LA POSITION DU DÉFILEMENT
	const updateScrollButtons = useCallback(() => {
		const track = eventsTrackRef.current;
		if (!track) return;
		setCanScrollPrev(track.scrollLeft > 4);
		setCanScrollNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
	}, []);

	useEffect(() => {
		updateScrollButtons();
		window.addEventListener("resize", updateScrollButtons);
		return () => window.removeEventListener("resize", updateScrollButtons);
	}, [futureEvents.length, updateScrollButtons]);

	const scrollEvents = (direction) => {
		const track = eventsTrackRef.current;
		if (!track) return;
		track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
	};

	const onHandleDisplayEvent = (e) => {
		dispatch({ type: EVENT_CATEGORY_SELECTED, payload: getCategoryLabel(e) })
		navigate('/eventsDetails')
	};


	//BANDEAU FLASH NEWS : les news défilent de droite à gauche, en boucle
	//- le texte est présent deux fois à la suite pour que la boucle soit continue (la 2e copie est ignorée par les lecteurs d'écran)
	//- la durée d'un tour dépend de la longueur du texte, pour garder une vitesse de lecture confortable
	//- pause au survol, et bouton pause / lecture (obligatoire pour un contenu qui bouge en continu)
	const flashTextLength = commingFlashNews.reduce((total, e) => total + (e.title || "").length + (e.bodyText || "").length + 10, 0);
	const flashTickerDuration = Math.max(15, Math.round(flashTextLength * 0.1));

	const flashNewsItems = commingFlashNews.map((e, index) => (
		<span key={index} className="flash_ticker_item">
			<strong>{e.title}</strong>{e.bodyText ? ` — ${e.bodyText}` : ""}
		</span>
	));

	const flashNewsBar = showFlashNews && (
		<div className="flash_bar" role="region" aria-label="Flash news">
			<div className="flash_bar_inner">
				<span className="flash_bar_tag">Flash</span>

				{/* Version lue par les lecteurs d'écran : chaque news une seule fois */}
				<ul className="sr-only">
					{commingFlashNews.map((e, index) => <li key={index}>{e.title}{e.bodyText ? ` — ${e.bodyText}` : ""}</li>)}
				</ul>

				<div className={`flash_ticker ${isFlashPaused ? "is-paused" : ""}`} aria-hidden="true">
					<div className="flash_ticker_track" style={{ "--ticker-duration": `${flashTickerDuration}s` }}>
						<div className="flash_ticker_group">{flashNewsItems}</div>
						<div className="flash_ticker_group">{flashNewsItems}</div>
					</div>
				</div>

				<button className="flash_bar_button flash_bar_pause" onClick={() => setIsFlashPaused(!isFlashPaused)} aria-label={isFlashPaused ? "Relancer le défilement des flash news" : "Mettre en pause le défilement des flash news"}>
					{isFlashPaused ? <LuPlay aria-hidden="true" /> : <LuPause aria-hidden="true" />}
				</button>
				<button className="flash_bar_button flash_bar_close" onClick={() => setShowFlashNews(false)} aria-label="Fermer la flash news">
					<LuX aria-hidden="true" />
				</button>
			</div>
		</div>
	);


	//CARTES ÉVÈNEMENTS
	const eventCards = futureEvents.map((e, index) => (
		<EventCard key={index} event={e} onSelect={onHandleDisplayEvent} />
	));


	return (
		<main className="home">
			{flashNewsBar}

			{/* BANDEAU D'ACCUEIL */}
			<section className="hero">
				<img className="hero_img" src="/images/photoGroupe2017VBLC169.jpg" alt="Les joueuses et joueurs du VBLC réunis au gymnase" />
				<div className="hero_scrim" aria-hidden="true" />
				<div className="hero_content">
					<p className="hero_eyebrow">
						<span className="tricolor hero_tricolor" aria-hidden="true"><span></span><span></span><span></span></span>
						Saint-Jean-de-Luz &amp; Ciboure
					</p>
					<h1 className="hero_title">Le volley au cœur de la <span className="hero_title_accent">Côte basque</span></h1>
					<p className="hero_text">Club de volley-ball et de beach-volley. Compétition, loisir ou corpo : il y a une place pour vous sur le terrain.</p>
					<div className="hero_actions">
						<Link to="/registration" className="btn btn--primary btn--lg">
							Rejoindre le club <LuArrowRight aria-hidden="true" />
						</Link>
						<Link to="/events" className="btn btn--outline-light btn--lg">Voir les évènements</Link>
					</div>
				</div>
			</section>

			{/* ACCÈS RAPIDES */}
			<nav className="wrap" aria-label="Accès rapides">
				<ul className="quick_links surface">
					{QUICK_LINKS.map(({ to, icon: Icon, tone, title, text }) => (
						<li key={to}>
							<Link to={to} className="quick_link">
								<span className={`icon-tile icon-tile--${tone}`} aria-hidden="true"><Icon /></span>
								<span className="quick_link_body">
									<span className="quick_link_title">{title}</span>
									<span className="quick_link_text">{text}</span>
								</span>
								<LuChevronRight className="quick_link_chevron" aria-hidden="true" />
							</Link>
						</li>
					))}
				</ul>
			</nav>

			{/* PROCHAINS ÉVÈNEMENTS */}
			<section className="section wrap" aria-labelledby="events-title">
				<div className="section_header">
					<div className="section_heading">
						<p className="eyebrow">Actualités</p>
						<h2 id="events-title" className="title-xl">À la une</h2>
					</div>
					<div className="section_actions">
						<Link to="/events" className="link-arrow">Toutes les actualités <LuArrowRight aria-hidden="true" /></Link>
						{/* Flèches seulement si les cartes débordent de la largeur */}
						{(canScrollPrev || canScrollNext) && (
							<div className="events_nav">
								<button className="btn btn--secondary btn--icon" onClick={() => scrollEvents(-1)} disabled={!canScrollPrev} aria-label="Actualités précédentes">
									<LuChevronLeft aria-hidden="true" />
								</button>
								<button className="btn btn--secondary btn--icon" onClick={() => scrollEvents(1)} disabled={!canScrollNext} aria-label="Actualités suivantes">
									<LuChevronRight aria-hidden="true" />
								</button>
							</div>
						)}
					</div>
				</div>

				{futureEvents.length > 0
					? <div className="events_track" ref={eventsTrackRef} onScroll={updateScrollButtons}>{eventCards}</div>
					: <div className="events_empty surface">
						<p>Aucune actualité pour le moment. Suivez-nous sur les réseaux sociaux pour ne rien manquer !</p>
					</div>}
			</section>

			{/* LE CLUB */}
			<section className="club_teaser" aria-labelledby="club-title">
				<div className="wrap club_teaser_inner">
					<div className="club_teaser_intro">
						<p className="eyebrow">Le club</p>
						<h2 id="club-title" className="title-xl">Un club,<br />trois façons de jouer</h2>
						<p className="club_teaser_text">En salle ou sur le sable, pour la compétition ou pour le plaisir : le VBLC accueille tous les niveaux à Saint-Jean-de-Luz et Ciboure.</p>
						<Link to="/club" className="link-arrow">Découvrir le club <LuArrowRight aria-hidden="true" /></Link>
					</div>
					<ul className="pillars">
						{CLUB_PILLARS.map(({ icon: Icon, tone, title, text }) => (
							<li key={title} className="pillar">
								<span className={`icon-tile icon-tile--${tone}`} aria-hidden="true"><Icon /></span>
								<div>
									<h3 className="title-md">{title}</h3>
									<p className="pillar_text">{text}</p>
								</div>
							</li>
						))}
					</ul>
				</div>
			</section>

			{/* APPEL À L'INSCRIPTION */}
			<section className="wrap section section--tight">
				<div className="join_band">
					<div className="join_band_text">
						<h2 className="join_band_title">Envie de rejoindre l’aventure ?</h2>
						<p>Inscription en ligne, pas à pas. Licence, tarifs et certificat médical : toutes les infos au même endroit.</p>
					</div>
					<div className="join_band_actions">
						<Link to="/registration" className="btn btn--light btn--lg">Je m’inscris</Link>
						<Link to="/registrationHelp" className="btn btn--outline-light btn--lg">Guide d’inscription</Link>
					</div>
				</div>
			</section>

			{/* PARTENAIRES */}
			<section className="wrap section" aria-labelledby="partners-title">
				<div className="section_header">
					<div className="section_heading">
						<p className="eyebrow">Partenaires</p>
						<h2 id="partners-title" className="title-xl">Ils nous soutiennent</h2>
					</div>
					<Link to="/contact" className="link-arrow">Devenir partenaire <LuArrowRight aria-hidden="true" /></Link>
				</div>
				<ul className="partners_grid">
					{PRIVATE_PARTNERS.map((p) => (
						<li key={p.partner}>
							<a className="partner_tile" href={p.webLink} target="_blank" rel="noreferrer">
								<img src={p.logoUrl} alt={p.partner} />
							</a>
						</li>
					))}
				</ul>
			</section>
		</main>
	)
};

export default Home;
